export const THEMES = {
  clean: {
    name: 'clean',
    displayName: 'Clean',
    primaryColor: '#0d6efd',
    accentColor: '#0a58ca',
    bgColor: '#ffffff',
    textColor: '#212529',
    cardBg: '#f8f9fa',
    borderColor: '#dee2e6',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  executive: {
    name: 'executive',
    displayName: 'Executive',
    primaryColor: '#f59e0b',
    accentColor: '#d97706',
    bgColor: '#0f2040',
    textColor: '#f1f5f9',
    cardBg: '#1a3460',
    borderColor: '#1e3a6e',
    fontFamily: '"Georgia", "Times New Roman", serif',
  },
  slate: {
    name: 'slate',
    displayName: 'Slate Pro',
    primaryColor: '#6366f1',
    accentColor: '#4f46e5',
    bgColor: '#0f172a',
    textColor: '#f8fafc',
    cardBg: '#1e293b',
    borderColor: '#334155',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  emerald: {
    name: 'emerald',
    displayName: 'Emerald',
    primaryColor: '#059669',
    accentColor: '#047857',
    bgColor: '#ffffff',
    textColor: '#064e3b',
    cardBg: '#ecfdf5',
    borderColor: '#a7f3d0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  midnight: {
    name: 'midnight',
    displayName: 'Midnight',
    primaryColor: '#06b6d4',
    accentColor: '#0891b2',
    bgColor: '#09090b',
    textColor: '#fafafa',
    cardBg: '#18181b',
    borderColor: '#27272a',
    fontFamily: '"Inter", system-ui, sans-serif',
  },
  crimson: {
    name: 'crimson',
    displayName: 'Crimson',
    primaryColor: '#dc2626',
    accentColor: '#b91c1c',
    bgColor: '#ffffff',
    textColor: '#1c0a0a',
    cardBg: '#fff5f5',
    borderColor: '#fecaca',
    fontFamily: 'system-ui, sans-serif',
  },
  violet: {
    name: 'violet',
    displayName: 'Violet',
    primaryColor: '#7c3aed',
    accentColor: '#6d28d9',
    bgColor: '#ffffff',
    textColor: '#1e1b4b',
    cardBg: '#f5f3ff',
    borderColor: '#ddd6fe',
    fontFamily: 'system-ui, sans-serif',
  },
  steel: {
    name: 'steel',
    displayName: 'Steel',
    primaryColor: '#38bdf8',
    accentColor: '#0284c7',
    bgColor: '#1c2333',
    textColor: '#e2e8f0',
    cardBg: '#263048',
    borderColor: '#334155',
    fontFamily: 'system-ui, sans-serif',
  },
  amber: {
    name: 'amber',
    displayName: 'Amber',
    primaryColor: '#d97706',
    accentColor: '#b45309',
    bgColor: '#fffbeb',
    textColor: '#451a03',
    cardBg: '#fef3c7',
    borderColor: '#fde68a',
    fontFamily: '"Georgia", serif',
  },
  graphite: {
    name: 'graphite',
    displayName: 'Graphite',
    primaryColor: '#ffffff',
    accentColor: '#e2e8f0',
    bgColor: '#18181b',
    textColor: '#f4f4f5',
    cardBg: '#27272a',
    borderColor: '#3f3f46',
    fontFamily: 'system-ui, sans-serif',
  },
};

const bgLuminance = hex => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

export const themeToVars = theme => {
  const isDark = bgLuminance(theme.bgColor) < 0.4;
  const text = theme.textColor;
  const border = theme.borderColor;
  const cardBg = theme.cardBg;

  const darkOverrides = isDark ? {
    // Remap Sharetribe's grey scale so text is readable on dark backgrounds
    '--colorGrey900': text,
    '--colorGrey800': text,
    '--colorGrey700': text,
    '--colorGrey600': theme.textColor + 'cc',  // slightly muted
    '--colorGrey500': theme.textColor + '99',
    '--colorGrey400': theme.textColor + '77',
    '--colorGrey300': border,
    '--colorGrey200': border,
    '--colorGrey100': cardBg,
    '--colorGrey50': cardBg,
    // Tab nav active underline and borders
    '--colorWhite': theme.bgColor,
    '--colorBlack': text,
  } : {};

  return {
    '--brandPrimary': theme.primaryColor,
    '--brandAccent': theme.accentColor,
    '--brandBg': theme.bgColor,
    '--brandText': text,
    '--brandCardBg': cardBg,
    '--brandBorder': border,
    '--brandFont': theme.fontFamily,
    '--marketplaceColor': theme.primaryColor,
    '--marketplaceColorDark': theme.accentColor,
    '--marketplaceColorLight': theme.primaryColor + '33',
    ...darkOverrides,
    backgroundColor: theme.bgColor,
    color: text,
    fontFamily: theme.fontFamily,
  };
};

// Returns white or black depending on which has better contrast against the given hex color
export const contrastColor = hex => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Perceived luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#111827' : '#ffffff';
};
